import React from 'react';
import { 
  Typography, 
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import PageTemplate from '../components/PageTemplate';
import {
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

const FAQPage: React.FC = () => {
  const generalFaqs = [
    {
      question: "What is 1TimeMail?",
      answer: "1TimeMail is a free temporary email service that provides disposable email addresses. These addresses can be used to sign up for websites, receive verification emails, or protect your privacy without revealing your personal email."
    },
    {
      question: "How long do temporary emails last?",
      answer: "For free users, temporary email addresses and all received messages are automatically deleted after 24 hours. Premium users can extend this period up to 7 days, and Business users up to 30 days."
    },
    {
      question: "Is 1TimeMail completely free?",
      answer: "Yes, our basic service is completely free with no hidden costs. We also offer Premium and Business plans with additional features for users who need enhanced functionality and longer email retention."
    },
    {
      question: "Do I need to create an account to use 1TimeMail?",
      answer: "No! That's one of the best features of 1TimeMail. You can instantly use our service without registration, sign-up, or providing any personal information. Simply visit our homepage and you'll get a temporary email address."
    },
    {
      question: "Can websites detect that I'm using a temporary email?",
      answer: "Some websites maintain lists of known temporary email domains and may block them. However, 1TimeMail regularly updates its domains to minimize this issue. Premium users also have access to custom domains that are less likely to be blocked."
    }
  ];
  
  const usageFaqs = [
    {
      question: "How do I get a temporary email address?",
      answer: "Simply visit the 1TimeMail homepage and an email address will be automatically generated for you. You can then copy it and use it wherever you need."
    },
    {
      question: "Can I choose my own email address?",
      answer: "Free users receive randomly generated addresses. Premium and Business users can create custom email addresses with their choice of username."
    },
    {
      question: "Can I receive attachments?",
      answer: "Yes, you can receive and download attachments. Free users can receive attachments up to 10MB, Premium users up to 25MB, and Business users up to 50MB."
    },
    {
      question: "Can I reply to emails I receive?",
      answer: "Yes, you can send replies from your temporary email address. Free accounts can send up to 3 replies per email address, while Premium and Business accounts have unlimited replies."
    },
    {
      question: "What happens if I receive an email after my temporary address expires?",
      answer: "Once your temporary email expires, any new emails sent to that address will bounce back to the sender. We don't store or forward emails after expiration for privacy and security reasons."
    }
  ];
  
  const privacyFaqs = [
    {
      question: "Is 1TimeMail secure?",
      answer: "Yes, we take security seriously. We use encryption for all communications and don't log the content of your emails. After the expiration period, all data is permanently deleted from our servers."
    },
    {
      question: "Does 1TimeMail track users or display ads?",
      answer: "We don't track users or build profiles. We have minimal analytics solely to improve our service. Free users may see non-personalized ads, but Premium and Business users have an ad-free experience."
    },
    {
      question: "Can law enforcement request access to emails?",
      answer: "We comply with valid legal requests, but our automatic deletion policy means we typically don't have data to provide after the email lifetime expires. We don't store IP addresses or other identifying information beyond what's necessary for the service to function."
    },
    {
      question: "Who should not use temporary emails?",
      answer: "Temporary emails should not be used for important accounts like banking, government services, primary social media accounts, or any service where you'll need long-term access or account recovery options."
    }
  ];
  
  const technicalFaqs = [
    {
      question: "What email clients are compatible with 1TimeMail?",
      answer: "1TimeMail works within your web browser, so there's no need for a separate email client. Our interface is optimized for all modern browsers on desktop and mobile devices."
    },
    {
      question: "Is there a limit to how many emails I can receive?",
      answer: "Free users can receive up to 50 emails per temporary address. Premium users can receive up to 200, and Business users have unlimited incoming emails."
    },
    {
      question: "Can I forward emails to my regular email address?",
      answer: "Email forwarding is available for Premium and Business users only. This feature allows you to automatically forward selected or all emails to your permanent email address."
    },
    {
      question: "Is there an API for 1TimeMail?",
      answer: "Yes, Business plan subscribers have access to our API that allows for programmatic creation and management of temporary email addresses, ideal for testing, development, and automation purposes."
    }
  ];

  const FAQ = ({ question, answer }: { question: string; answer: string }) => (
    <Accordion 
      elevation={0}
      sx={{ 
        mb: 1,
        '&::before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ 
          backgroundColor: theme => theme.palette.mode === 'light' 
            ? 'rgba(0, 0, 0, 0.02)' 
            : 'rgba(255, 255, 255, 0.02)',
          '&:hover': {
            backgroundColor: theme => theme.palette.mode === 'light' 
              ? 'rgba(0, 0, 0, 0.04)' 
              : 'rgba(255, 255, 255, 0.04)',
          }
        }}
      >
        <Typography variant="subtitle1" fontWeight={500}>
          {question}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary">
          {answer}
        </Typography>
      </AccordionDetails>
    </Accordion>
  );

  const FAQSection = ({ title, description, faqs }: { title: string; description: string; faqs: { question: string; answer: string }[] }) => (
    <Box sx={{ mb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" component="h2" fontWeight={600}>
            {title}
          </Typography>
          <Chip 
            label={`${faqs.length} questions`} 
            size="small" 
            sx={{ ml: 2 }} 
            color="default" 
            variant="outlined" 
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      
      {faqs.map((faq, index) => (
        <FAQ key={index} question={faq.question} answer={faq.answer} />
      ))}
    </Box>
  );

  return (
    <PageTemplate 
      title="Frequently Asked Questions" 
      description="Find answers to common questions about 1TimeMail's temporary email service."
      breadcrumbs={[{ label: 'FAQ', path: '/faq' }]}
    >
      <Box sx={{ mb: 6 }}>
        <Typography variant="body1" color="text.secondary">
          Have a question that's not answered here? <a href="/contact" style={{ color: 'inherit', fontWeight: 600 }}>Contact our support team</a> for assistance.
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <FAQSection 
            title="General Questions" 
            description="Basic information about 1TimeMail and our services."
            faqs={generalFaqs} 
          />
        </Grid>
        
        <Grid item xs={12} lg={6}>
          <FAQSection 
            title="Using 1TimeMail" 
            description="How to get the most out of our temporary email service."
            faqs={usageFaqs} 
          />
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 4 }} />
      
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <FAQSection 
            title="Privacy & Security" 
            description="Information about how we protect your data and privacy."
            faqs={privacyFaqs} 
          />
        </Grid>
        
        <Grid item xs={12} lg={6}>
          <FAQSection 
            title="Technical Questions" 
            description="Details about the technical aspects of our service."
            faqs={technicalFaqs} 
          />
        </Grid>
      </Grid>
    </PageTemplate>
  );
};

export default FAQPage; 